import { RefObject, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";

export type UseWebAudioPlayerResult = {
  playerRef: RefObject<ReactPlayer>;
  isInitialised: boolean;
  playTime: number;
  duration: number;
  isPlaying: boolean;
  error: boolean;
  play: () => void;
  pause: () => void;
  stop: () => void;
  onSeekMouseDown: () => void;
  onSeekMouseUp: () => void;
  onSeekChange: (position: number) => void;
  sourceHandlers: {
    onPlay: () => void;
    onPause: () => void;
    onEnded: () => void;
    onDuration: (duration: number) => void;
    onProgress: (progress: { played: number }) => void;
    onError: () => void;
  };
};

export const useWebAudioPlayer = (autoStart = true): UseWebAudioPlayerResult => {
  const playerRef = useRef<ReactPlayer>(null);
  const [playTime, setPlayTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoStart);
  const [isSeeking, setIsSeeking] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isSeeking && duration > 0) {
      playerRef.current?.seekTo(playTime / duration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSeeking]);

  const play = () => setIsPlaying(true);
  const pause = () => setIsPlaying(false);
  const stop = () => {
    setIsPlaying(false);
    setPlayTime(0);
    playerRef.current?.seekTo(0);
  };
  const onSeekMouseDown = () => setIsSeeking(true);
  const onSeekMouseUp = () => setIsSeeking(false);
  const onSeekChange = (position: number) => setPlayTime(Math.min(duration, duration * position));

  return {
    playerRef,
    isInitialised: duration > 0,
    playTime,
    duration,
    isPlaying,
    error,
    play,
    pause,
    stop,
    onSeekMouseDown,
    onSeekMouseUp,
    onSeekChange,
    sourceHandlers: {
      onPlay: () => setIsPlaying(true),
      onPause: () => setIsPlaying(false),
      onEnded: () => setIsPlaying(false),
      onDuration: setDuration,
      onProgress: ({ played }) => {
        if (!isSeeking) {
          setPlayTime(played * duration);
        }
      },
      onError: () => setError(true),
    },
  };
};

export type WebAudioSourceProps = {
  src: string;
  player: UseWebAudioPlayerResult;
};

export const WebAudioSource = ({ src, player }: WebAudioSourceProps) => (
  <div className="hidden">
    <ReactPlayer ref={player.playerRef} width="100%" height="100%" url={src} playing={player.isPlaying} controls={false} {...player.sourceHandlers} />
  </div>
);
